import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ContinuumTriangle from "@/components/ContinuumTriangle";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Printer, Sprout, Send } from "lucide-react";

const LEVEL_LABELS = { classroom: "Classroom Support", school: "School Support", school_plus: "School Support Plus" };
const DECISION_LABELS = {
  continue: "Continue at current level", progress: "Progress to a higher level",
  revert: "Revert to a lower level", consult: "Request consultation with professionals",
  close: "Close file (needs met)",
  maintain: "Continue at current level", step_up: "Progress to a higher level",
  step_down: "Revert to a lower level", fade: "Close file (needs met)",
};

export default function CosDocument() {
  const { studentId, docType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const reviewId = new URLSearchParams(location.search).get("review");

  const sendForSigning = async () => {
    setSending(true);
    try {
      const dt = docType === "review" ? "cos_review" : "cos_plan";
      const { data: share } = await api.post("/parent-shares", { student_id: studentId, doc_type: dt });
      await navigator.clipboard.writeText(`${window.location.origin}/p/${share.token}`).catch(() => {});
      toast.success(`Parent link copied. Access code: ${share.access_code}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  useEffect(() => {
    api.get(`/students/${studentId}/cos`).then((r) => setData(r.data)).catch(() => navigate("/"));
  }, [studentId, navigate]);

  if (!data) return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const isReview = docType === "review";
  const snapReview = reviewId ? (data.plan.reviews || []).find((r) => r.id === reviewId) : null;
  const snap = snapReview?.snapshot;
  const fullRecord = !!snap;
  const isFull = docType === "full";
  const plan = snap ? snap.plan : data.plan;
  const student = snap ? snap.student : data.student;
  const review = snap ? snap.review : (data.plan.reviews || [])[0];
  const allReviews = [...(data.plan.reviews || [])].reverse();
  const docTests = snap ? (snap.tests || []) : (data.tests || []);
  const CHECKLIST_TITLES = { basic_needs: "Basic needs (Maslow)", support_checklist: "Support checklist" };
  const today = new Date().toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" });

  const planBody = (
    <>
      <Block title="Strengths & interests">{[plan.strengths, plan.interests].filter(Boolean).join("\n\n")}</Block>
      <Block title="Priority needs / areas of concern">{[plan.priority_needs, plan.needs].filter(Boolean).join("\n\n")}</Block>
      <Block title="Possible reasons for concerns">{plan.possible_reasons}</Block>
      <Block title="Barriers to learning">{plan.barriers}</Block>
      <Section title="Targets, strategies & supports">
        {(plan.targets || []).length === 0 && <p className="text-sm text-slate-400">No targets recorded.</p>}
        {(plan.targets || []).map((t, i) => (
          <div key={t._k || `${t.subject}-${t.area}-${i}`} className="mb-3 pb-3 border-b border-slate-100 last:border-0">
            <p className="font-semibold text-slate-800 text-sm">{[t.subject, t.area].filter(Boolean).join(" — ") || `Target ${i + 1}`}</p>
            {t.target && <p className="text-sm text-slate-600 mt-0.5">{t.target}</p>}
            {t.strategies && <p className="text-sm text-slate-500 mt-1"><b>Strategies:</b> {t.strategies}</p>}
            <div className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-4">
              {t.resources && <span><b>Resources:</b> {t.resources}</span>}
              {t.responsibility && <span><b>Who:</b> {t.responsibility}</span>}
            </div>
          </div>
        ))}
      </Section>
      <Block title="Staff involved & resources needed">{plan.personnel}</Block>
      <Block title="Additional notes / home-school links">{plan.notes}</Block>
      {docTests.length > 0 && (
        <Section title="Standardised & diagnostic tests">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="py-1 pr-3">Date</th><th className="py-1 pr-3">Assessment</th>
              <th className="py-1 pr-3">Result</th><th className="py-1">Risk band</th>
            </tr></thead>
            <tbody>
              {docTests.map((t) => (
                <tr key={`${t.date}-${t.name}`} className="border-t border-slate-100">
                  <td className="py-1 pr-3 text-slate-500">{t.date || "—"}</td>
                  <td className="py-1 pr-3 text-slate-700">{t.name}</td>
                  <td className="py-1 pr-3 text-slate-700">{t.result || "—"}</td>
                  <td className="py-1 text-slate-500">{t.risk_band || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
      {Object.entries(plan.checklists || {}).some(([, v]) => (v || []).length) && (
        <Section title="Basic needs & support checklists">
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(plan.checklists || {}).filter(([, v]) => (v || []).length).map(([g, items]) => (
              <div key={g}>
                <p className="font-semibold text-slate-700 text-sm mb-1">{CHECKLIST_TITLES[g] || g}</p>
                <ul className="text-sm text-slate-600 list-disc list-inside">
                  {items.map((it) => <li key={it}>{it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}
      <Block title="Possible actions to meet basic needs">{plan.basic_needs_actions}</Block>
    </>
  );

  const renderReview = (r) => (
    <>
      <div className="text-sm text-slate-600 mt-2"><b>Review date:</b> {r.date} &nbsp;·&nbsp; <b>Outcome:</b> {DECISION_LABELS[r.decision] || r.decision}</div>
      <Block title="Present at review">{r.present}</Block>
      {(r.target_outcomes || []).length > 0 && (
        <Section title="Target outcomes">
          {r.target_outcomes.map((o, i) => (
            <div key={`${o.subject}-${o.area}-${i}`} className="mb-2 pb-2 border-b border-slate-100 last:border-0">
              <p className="font-semibold text-slate-800 text-sm">{[o.subject, o.area].filter(Boolean).join(" — ")}</p>
              {o.target && <p className="text-sm text-slate-600">{o.target}</p>}
              <p className="text-sm text-slate-500 mt-0.5"><b>Outcome:</b> {o.outcome}{o.evidence ? ` — ${o.evidence}` : ""}</p>
            </div>
          ))}
        </Section>
      )}
      <Block title="Most successful areas & why">{r.most_successful || r.what_worked}</Block>
      <Block title="Changes to the original concerns">{r.concerns_changed || r.what_to_change}</Block>
      <Block title="Have the pupil's needs changed?">{r.needs_changed}</Block>
      <Block title="Recommended future actions">{r.future_actions || r.next_steps}</Block>
      <Block title="Comments from the pupil">{r.student_comment}</Block>
      <Block title="Comments from parent(s)/guardian(s)">{r.parent_comment}</Block>
    </>
  );

  const reviewBody = review ? renderReview(review) : (
    <p className="text-sm text-slate-400 mt-6">No review has been recorded yet. Add one from the Review step of the plan.</p>
  );

  const heading = (fullRecord || isFull) ? "Student Support File" : isReview ? "Support Review Record" : "Student Support Plan";

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between no-print">
        <button data-testid="cos-doc-back-btn" onClick={() => navigate(`/student/${studentId}/cos`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> Back to plan
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="cos-doc-print-btn" onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold font-['Outfit'] text-white" style={{ background: "#44546A" }}>
            <Printer size={16} /> Print / Save PDF
          </button>
          {!fullRecord && !isFull && (
            <button data-testid="cos-doc-send-btn" onClick={sendForSigning} disabled={sending}
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
              style={{ background: "linear-gradient(135deg,#7FB069,#5d8a5a)" }}>
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send for signing
            </button>
          )}
        </div>
      </div>

      <div className="print-area max-w-3xl mx-auto bg-white rounded-xl shadow-sm print:shadow-none p-8 sm:p-10" data-testid="cos-document">
        {/* Letterhead */}
        <div className="flex items-center justify-between border-b-2 border-slate-200 pb-4 mb-6">
          <div className="flex items-center gap-2.5">
            {user?.school_logo
              ? <img src={user.school_logo} alt="" className="h-11 w-11 rounded-lg object-cover" />
              : <span className="h-11 w-11 rounded-lg bg-[#7FB069] flex items-center justify-center text-white"><Sprout size={20} /></span>}
            <div>
              <p className="font-['Outfit'] font-bold text-slate-800 leading-tight">{user?.school_name || "Primary School"}</p>
              <p className="text-xs text-slate-400">Continuum of Support</p>
            </div>
          </div>
          <p className="text-xs text-slate-400">{today}</p>
        </div>

        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-800">{heading}</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
          <span><b>Pupil:</b> {student.name}</span>
          <span><b>Class:</b> {student.class_level}{student.class_name ? ` · ${student.class_name}` : ""}</span>
          <span><b>Level:</b> {LEVEL_LABELS[plan.level] || plan.level}</span>
          {!isReview && plan.review_date && <span><b>Review date:</b> {plan.review_date}</span>}
        </div>

        {isFull && (
          <div className="mt-4 rounded-xl border border-slate-200 p-4 grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm text-slate-600" data-testid="cos-full-cover">
            {plan.start_date && <span><b>Date file opened:</b> {plan.start_date}</span>}
            <span><b>Date generated:</b> {today}</span>
            <span><b>Reviews on file:</b> {allReviews.length}</span>
            <span><b>Status:</b> {plan.status === "closed" ? "Closed (needs met)" : "Active"}</span>
          </div>
        )}

        {!isReview && (
          <div className="mt-5" data-testid="cos-doc-triangle"><ContinuumTriangle level={plan.level} /></div>
        )}

        {fullRecord ? (
          <>
            {planBody}
            <div className="mt-10 pt-6 border-t-2 border-slate-200">
              <h2 className="font-['Outfit'] text-xl font-bold text-slate-800 mb-1">Support Review Record</h2>
            </div>
            {reviewBody}
          </>
        ) : isFull ? (
          <>
            {planBody}
            {allReviews.length > 0 && allReviews.map((r, i) => (
              <div key={r.id || i} className="mt-10 pt-6 border-t-2 border-slate-200" style={{ breakBefore: "page" }} data-testid={`full-review-${i}`}>
                <h2 className="font-['Outfit'] text-xl font-bold text-slate-800 mb-1">Support Review Record {allReviews.length > 1 ? `(${i + 1} of ${allReviews.length})` : ""}</h2>
                {renderReview(r)}
              </div>
            ))}
          </>
        ) : isReview ? reviewBody : planBody}

        {/* Signatures */}
        <div className="mt-10 pt-6 border-t border-slate-200 grid sm:grid-cols-2 gap-8">
          <SignLine label="Class Teacher" />
          <SignLine label="Special Education Teacher" />
          <SignLine label="Parent / Guardian" />
          <SignLine label="Date" />
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{title}</p>
      {children}
    </div>
  );
}
function Block({ title, children }) {
  if (!children || !String(children).trim()) return null;
  return (
    <div className="mt-6">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">{title}</p>
      <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{children}</p>
    </div>
  );
}
function SignLine({ label }) {
  return (
    <div>
      <div className="h-8 border-b border-slate-300" />
      <p className="text-xs text-slate-500 mt-1.5">{label}</p>
    </div>
  );
}
